import { flightsApi, type ExtractedFlightGroup } from './flights.api';
import { hotelsApi } from './hotels.api';
import { bookingsApi } from './bookings.api';
import { localInputToISO } from '../utils/dateTime';
import type { BookingType } from '../types';

/** The Docs tab's four sections, which is how the user sorts their tickets. */
export type TicketKind = 'flight' | 'hotel' | 'transport' | 'event';
export const TICKET_KINDS: TicketKind[] = ['flight', 'hotel', 'transport', 'event'];
export type TicketFiles = Record<TicketKind, File[]>;

/** The extract endpoints take up to 5 files per call (multer `upload.array('files', 5)`). */
export const MAX_TICKET_FILES_PER_KIND = 5;

export const EMPTY_TICKETS: TicketFiles = { flight: [], hotel: [], transport: [], event: [] };

export function ticketCount(files: TicketFiles): number {
  return TICKET_KINDS.reduce((n, k) => n + files[k].length, 0);
}

// The extractor answers in the same wall-clock 'YYYY-MM-DDTHH:mm…' the Docs
// forms put in their inputs, so it is stored the way those forms store it.
const wallClock = (s: string) => localInputToISO(s.slice(0, 16));
const dayISO = (s: string) => new Date(s.slice(0, 10)).toISOString();

/** Each file goes on the ticket it produced, as the Docs form does on save. A failed upload loses only that copy. */
async function attach(files: File[], upload: (file: File) => Promise<unknown>): Promise<void> {
  for (const file of files) {
    try { await upload(file); } catch (err) { console.warn('[tickets] attachment skipped:', err); }
  }
}

function flightLabel(group: ExtractedFlightGroup): string {
  return `${group.legs[0].fromAirport} → ${group.legs[group.legs.length - 1].toAirport}`;
}

async function importFlights(tripId: string, files: File[]): Promise<string[]> {
  const { flights } = await flightsApi.extract(tripId, files);
  const labels: string[] = [];
  // A round-trip ticket is two journeys; the document goes on both.
  for (const group of flights.filter(g => g.legs.length > 0)) {
    const first = group.legs[0];
    const last = group.legs[group.legs.length - 1];
    const flight = await flightsApi.create(tripId, {
      direction: group.direction ?? 'OUTBOUND',
      notes: group.bookingRef ? `PNR: ${group.bookingRef}` : undefined,
      departureAirport: first.fromAirport.toUpperCase(),
      arrivalAirport: last.toAirport.toUpperCase(),
      departureTime: wallClock(first.departureTime),
      arrivalTime: wallClock(last.arrivalTime),
      flightNumber: first.flightNumber ?? null,
      airline: first.airline ?? null,
      stops: group.legs.map(l => ({
        fromAirport: l.fromAirport.toUpperCase(),
        toAirport: l.toAirport.toUpperCase(),
        departureTime: wallClock(l.departureTime),
        arrivalTime: wallClock(l.arrivalTime),
        flightNumber: l.flightNumber ?? null,
        airline: l.airline ?? null,
      })),
    });
    await attach(files, f => flightsApi.uploadDocument(tripId, flight.id, f));
    labels.push(flightLabel(group));
  }
  return labels;
}

async function importHotel(tripId: string, files: File[]): Promise<string[]> {
  const extracted = await hotelsApi.extract(tripId, files);
  if (!extracted.name || !extracted.checkIn || !extracted.checkOut) return [];
  const hotel = await hotelsApi.create(tripId, {
    name: extracted.name,
    address: extracted.address ?? null,
    checkIn: wallClock(extracted.checkIn),
    checkOut: wallClock(extracted.checkOut),
    notes: extracted.bookingRef ? `PNR: ${extracted.bookingRef}` : null,
  });
  await attach(files, f => hotelsApi.uploadDocument(tripId, hotel.id, f));
  return [extracted.name];
}

async function importBooking(tripId: string, files: File[], kind: 'transport' | 'event'): Promise<string[]> {
  const extracted = await bookingsApi.extract(tripId, files);
  if (!extracted.title || !extracted.date) return [];
  // Same rule as BookingForm: the section the user chose wins over the AI's guess.
  const type: BookingType = kind === 'transport'
    ? 'TRANSPORT'
    : (extracted.type && extracted.type !== 'TRANSPORT' ? extracted.type : 'EVENT');
  const date = extracted.date.slice(0, 10);
  const endDate = extracted.endDate?.slice(0, 10);
  const booking = await bookingsApi.create(tripId, {
    type,
    title: extracted.title,
    date: dayISO(date),
    endDate: endDate && endDate > date ? dayISO(endDate) : null,
    startTime: extracted.startTime ?? null,
    endTime: extracted.endTime ?? null,
    venue: extracted.venue ?? null,
    address: extracted.address ?? null,
    notes: extracted.bookingRef ? `Ref: ${extracted.bookingRef}` : null,
  });
  await attach(files, f => bookingsApi.uploadDocument(tripId, booking.id, f));
  return [extracted.title];
}

const IMPORTERS: Record<TicketKind, (tripId: string, files: File[]) => Promise<string[]>> = {
  flight: importFlights,
  hotel: importHotel,
  transport: (tripId, files) => importBooking(tripId, files, 'transport'),
  event: (tripId, files) => importBooking(tripId, files, 'event'),
};

/**
 * The wizard's tickets, saved without review (the wizard is the accept): one
 * extraction per kind — one AI credit each — through the same endpoints as
 * «Add with AI» in Docs, with the files attached to what they produced.
 * Returns the files that produced nothing, so the caller can keep them.
 */
export async function importTickets(
  tripId: string,
  files: TicketFiles,
  onSaved: (label: string) => void,
): Promise<File[]> {
  const unplaced: File[] = [];
  for (const kind of TICKET_KINDS) {
    if (files[kind].length === 0) continue;
    try {
      const labels = await IMPORTERS[kind](tripId, files[kind]);
      if (labels.length === 0) unplaced.push(...files[kind]);
      labels.forEach(onSaved);
    } catch (err) {
      console.warn(`[tickets] ${kind} extraction failed:`, err);
      unplaced.push(...files[kind]);
    }
  }
  return unplaced;
}
